import React, { Fragment, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Row, Col, Form } from 'react-bootstrap';

import {
  requestInsert,
  requestUpdate,
  requestInform as request
} from 'api/admin';
import Content from 'components/common-sfims/Content';
import Select from 'components/common-sfims/form/Select';
import List from 'components/common-sfims/List';
import { array2ValueAndText, utc2tz } from 'function/convert';
import { onInputChange } from 'function/event';
import { getText } from 'function/language';
import { getLoginInform } from 'function/login';
import { getErrorMessage } from 'function/message';
import { isValidRequired } from 'function/valid';
import useBUState from 'global/buState';
import { useConfirmModalState } from 'global/confirmModalState';
import usePlantState from 'global/plantState';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE, ROUTER_PATH } from 'types/const';
import { RETURN_YN, RETURN_YN_SELECT } from 'types/type';
import useLoginState from 'global/loginState';

/**
 * 관리자 페이지 - 등록/수정
 */
const Setting = () => {
  /* variable */
  const { seq: loginSeq } = getLoginInform();

  /* router */
  const history = useHistory();
  const { seq } = useParams();

  /* recoil */
  const { bu } = useBUState();
  const {
    plant: { alls: plants }
  } = usePlantState();
  const { setConfirmModal } = useConfirmModalState();
  const { setLogin } = useLoginState();

  /* state */
  // 사용자 등록/수정 요청 데이터
  const [adminData, setAdminData] = useObjReducer({
    userId: '',
    userNm: '',
    buCode: '',
    email: '',
    useYn: '',
    factoryCodeSeq: 0,
    authList: [],
    haccpYn: RETURN_YN.N
  });
  // 사용자 정보
  const [inform, setInform] = useObjReducer({});
  // 설정 비활성화 여부
  const [isDisableSetting, setIsDisableSetting] = useState(false);

  /* watch */
  useEffect(() => {
    // 수정 화면인 경우 사용자 정보 조회 요청
    if (seq) {
      requestInform();
    }
  }, []);
  useEffect(() => {
    const { pltCode } =
      plants.find(({ seq } = {}) => seq === adminData.factoryCodeSeq) ?? {};

    // 기본 조회 공장의 조회 권한이 없는 경우 기본 조회 공장 초기화
    if (
      adminData.authList.find(({ pltCode: plant } = {}) => plant === pltCode)
        ?.readYn !== RETURN_YN.Y
    ) {
      setAdminData({ factoryCodeSeq: 0 });
    }
  }, [adminData.authList]);
  useEffect(() => {
    if (plants.length > 0) {
      setAdminData({ authList: addPlantsToAuths() });
    }
  }, [plants]);

  /* method */
  // 전체 공장 목록을 권한 목록에 추가
  const addPlantsToAuths = () =>
    plants.map(({ pltCode, pltCd } = {}) => {
      const { readYn = RETURN_YN.N, modYn = RETURN_YN.N } =
        adminData.authList?.find?.(
          ({ pltCode: code } = {}) => code === pltCode
        ) ?? {};

      return {
        pltCode,
        pltCd,
        readYn,
        modYn
      };
    });
  // 사용자 정보 조회 요청
  const requestInform = async () => {
    try {
      const { data = {}, code, message } = await request(seq);

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setAdminData({
        userId: data.userId ?? '',
        userNm: data.userNm ?? '',
        buCode: data.buCode ?? '',
        email: data.email ?? '',
        useYn: data.useYn ?? '',
        factoryCodeSeq: data.factoryCodeSeq ?? 0,
        authList: (data.authList ?? []).map((value = {}) => {
          delete value.seq;

          return value;
        }),
        haccpYn: data.haccpYn ?? RETURN_YN.N
      });
      setInform(data);
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });

      // 목록 화면 표시
      showList();
    }
  };
  // 목록 화면 표시
  const showList = () => {
    history.push(ROUTER_PATH.admin.list);
  };

  /* event */
  // 전체 체크 변경 시
  const onAllCheckChange = ({ target: { checked, name } }) => {
    const { authList } = adminData;

    setAdminData({
      authList: plants.map(({ pltCode } = {}) => ({
        ...authList.find(({ pltCode: code } = {}) => code === pltCode),
        [name]: RETURN_YN[checked ? 'Y' : 'N']
      }))
    });
  };
  // 체크 변경 시
  const onCheckChange = ({ target: { checked, name, value } }) => {
    const yn = RETURN_YN[checked ? 'Y' : 'N'];

    let { authList } = adminData;
    let index = -1;

    do {
      index = authList.findIndex(({ pltCode } = {}) => value === pltCode);

      if (index < 0) {
        authList = addPlantsToAuths();
      }
    } while (index < 0);

    authList[index][name] = yn;

    setAdminData({ authList });
  };
  // 설정
  const onSubmit = async event => {
    const data = { ...adminData };

    let title = 'completeInsert';
    let message = 'completeInsertUser';

    event.preventDefault();

    if (seq) {
      title = 'completeUpdate';
      message = 'completeUpdateMessage';
    }

    setIsDisableSetting(true);

    try {
      // 필수 항목 입력 유효성 확인
      if (!isValidRequired(data, ['authList'])) {
        throw {
          code: RESPONSE_CODE[seq ? 'BadRequestUpdate' : 'BadRequestInsert']
        };
      }

      if (seq) {
        const { code, message } = await requestUpdate(seq, data);

        if (code !== RESPONSE_CODE.Success) {
          throw {
            code,
            error: message
          };
        }

        // 로그인 사용자 수정 시 로그인 정보 재설정
        if (loginSeq === Math.floor(seq)) {
          setLogin({
            fatoryCodeSeq: data.factoryCodeSeq,
            authList: data.authList,
            haccpYn: data.haccpYn
          });
        }
      } else {
        const { code, message } = await requestInsert(data);

        if (code !== RESPONSE_CODE.Success) {
          // 아이디 중복 시
          if (code === RESPONSE_CODE.DuplicateInsert && message === 'user_id') {
            throw { code: 'duplicateUserId' };
          }

          throw {
            code,
            error: message
          };
        }
      }

      // 목록 화면 표시
      showList();
    } catch (error) {
      title = 'notice';
      message = getErrorMessage(error?.code, error?.error);

      setIsDisableSetting(false);
    } finally {
      setConfirmModal({
        isOpen: true,
        title,
        message
      });
    }
  };

  return (
    <Form onSubmit={onSubmit}>
      <Content>
        <Fragment key="title">{getText(seq ? 'update' : 'insert')}</Fragment>
        <Fragment key="body">
          <Row className="g-3 mb-3">
            <Col sm={4}>
              <Form.Group>
                <Form.Label>* {getText('userId')}</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={adminData.userId}
                  autoFocus
                  maxLength={20}
                  name="userId"
                  placeholder={getText('userIdPlaceholder')}
                  required
                  onChange={event => onInputChange(event, setAdminData)}
                />
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group>
                <Form.Label>* {getText('userNm')}</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={adminData.userNm}
                  maxLength={50}
                  name="userNm"
                  placeholder={getText('userNmPlaceholder')}
                  required
                  onChange={event => onInputChange(event, setAdminData)}
                />
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group>
                <Form.Label>* {getText('userBuName')}</Form.Label>
                <Select
                  datas={array2ValueAndText(bu, 'buCode', 'buName')}
                  placeholder="userBuName"
                  value={adminData.buCode}
                  className="w-100"
                  name="buCode"
                  required
                  onChange={event => onInputChange(event, setAdminData)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="g-3 mb-3">
            <Col sm={4}>
              <Form.Group>
                <Form.Label>* {getText('email')}</Form.Label>
                <Form.Control
                  size="sm"
                  type="email"
                  value={adminData.email}
                  name="email"
                  required
                  onChange={event => onInputChange(event, setAdminData)}
                />
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group>
                <Form.Label>* {getText('useYn')}</Form.Label>
                <Select
                  datas={array2ValueAndText(RETURN_YN_SELECT, 'key', 'value')}
                  placeholder="useYn"
                  value={adminData.useYn}
                  className="w-100"
                  name="useYn"
                  required
                  onChange={event => onInputChange(event, setAdminData)}
                />
              </Form.Group>
            </Col>
            {seq && (
              <Col sm={4}>
                <Form.Group>
                  <Form.Label>{getText('loginDt')}</Form.Label>
                  <Form.Control
                    readOnly
                    size="sm"
                    type="text"
                    value={utc2tz(inform.loginDt)}
                  />
                </Form.Group>
              </Col>
            )}
          </Row>
          <List
            className={{ table: 'mt-3' }}
            columns={[
              {
                text: 'pltCode',
                className: 'w-50'
              },
              `* ${getText('dfRead')}`,
              'readYn',
              'modYn'
            ]}
          >
            <tr className="table-primary">
              <td>{getText('all')}</td>
              <td></td>
              <td>
                <Form.Check
                  checked={plants.every(
                    ({ pltCode } = {}) =>
                      adminData.authList.find(
                        ({ pltCode: code } = {}) => code === pltCode
                      )?.readYn === RETURN_YN.Y
                  )}
                  name="readYn"
                  onChange={onAllCheckChange}
                />
              </td>
              <td>
                <Form.Check
                  checked={plants.every(
                    ({ pltCode } = {}) =>
                      adminData.authList.find(
                        ({ pltCode: code } = {}) => code === pltCode
                      )?.modYn === RETURN_YN.Y
                  )}
                  name="modYn"
                  onChange={onAllCheckChange}
                />
              </td>
            </tr>
            {plants.map(({ seq, pltCode } = {}) => {
              const { readYn, modYn } =
                adminData.authList.find(
                  ({ pltCode: code } = {}) => code === pltCode
                ) ?? {};

              return (
                <tr key={`plants.${pltCode}`}>
                  <td>{pltCode}</td>
                  <td>
                    <Form.Check
                      disabled={readYn !== RETURN_YN.Y}
                      type="radio"
                      checked={seq === adminData.factoryCodeSeq}
                      name="factoryCodeSeq"
                      required
                      value={seq}
                      onChange={event => {
                        onInputChange(event, setAdminData, {
                          convert: 'number'
                        });
                      }}
                    />
                  </td>
                  <td>
                    <Form.Check
                      checked={readYn === RETURN_YN.Y}
                      name="readYn"
                      value={pltCode}
                      onChange={onCheckChange}
                    />
                  </td>
                  <td>
                    <Form.Check
                      checked={modYn === RETURN_YN.Y}
                      name="modYn"
                      value={pltCode}
                      onChange={onCheckChange}
                    />
                  </td>
                </tr>
              );
            })}
          </List>
        </Fragment>
        <Fragment key="footerButton">
          <Button
            size="sm"
            variant="secondary"
            className="ms-auto"
            onClick={showList}
          >
            {getText('cancel')}
          </Button>
          <Button
            disabled={isDisableSetting}
            size="sm"
            type="submit"
            className="ms-2"
          >
            {getText('ok')}
          </Button>
        </Fragment>
      </Content>
    </Form>
  );
};

export default Setting;
